import { startOfDay } from 'date-fns'
import { useTranslation } from 'next-i18next'
import { useMemo } from 'react'
import { match, P } from 'ts-pattern'
import { isNotNullOrUndefined } from 'helpers/array'
import {
  raceCategoriesLevel1,
  triathlonLevel2Categories
} from 'helpers/raceCategoryLevels'
import { AutocompleteItem } from 'interfaces/algolia'
import { unifiedColors } from 'styles/colors'
import { useEventCardDisplayedDate } from './useEventCardDistplayedDate'

export const getEventTypeStyle = (type: keyof typeof raceCategoriesLevel1) =>
  match(type)
    .returnType<{
      value: string
      color: keyof typeof unifiedColors.label
      backgroundColor: keyof typeof unifiedColors.surface
    } | null>()
    .with(P.union('FUN', 'WALK', 'OTHER'), () => ({
      value: type,
      color: 'onPink',
      backgroundColor: 'pink'
    }))
    .with(P.union('CYCLING', 'TRIATHLON'), () => ({
      value: type,
      color: 'onBlue',
      backgroundColor: 'blue'
    }))
    .with('RUNNING', () => ({
      value: type,
      color: 'onPurple',
      backgroundColor: 'purple'
    }))
    .with('TRAIL', () => ({
      value: type,
      color: 'onGreen',
      backgroundColor: 'green'
    }))
    .otherwise(() => null)

const triathlonLabelsOrder = [
  'ULTRA_TRIATHLON',
  'TRIATHLON_XXL',
  'TRIATHLON_L',
  'TRIATHLON_M',
  'TRIATHLON_S',
  'TRIATHLON_XS',
  'TRIATHLON_KIDS',
  'DUATHLON',
  'AQUATHLON',
  'RUN_BIKE',
  'SWIM_RUN',
  'SWIM_BIKE',
  'CROSS_TRIATHLON'
]

export const useEventCard = (hit: AutocompleteItem) => {
  const { t } = useTranslation('common')

  const {
    editionLiveStartDateTimestamp,
    editionLiveEndDateTimestamp,
    editionCalendarStatus,
    eventLiveDistanceKm,
    editionLiveLevel1CategoryKey,
    editionLiveLevel2CategoryKey
  } = hit

  const { dateDisplayText, endDate, startDate } = useEventCardDisplayedDate({
    editionLiveStartDateTimestamp,
    editionLiveEndDateTimestamp,
    editionCalendarStatus
  })

  const triathlonEventLevel2Categories = (editionLiveLevel2CategoryKey ?? [])
    .filter((category) =>
      (triathlonLevel2Categories as readonly string[]).includes(category)
    )
    .sort(
      (a, b) =>
        triathlonLabelsOrder.indexOf(a) - triathlonLabelsOrder.indexOf(b)
    )
    .map((category) =>
      t(category as (typeof triathlonLevel2Categories)[number])
    )
  const distanceLabels = (
    !editionLiveLevel1CategoryKey.includes('TRIATHLON')
      ? eventLiveDistanceKm
      : []
  )
    .filter((dist) => parseFloat(dist) > 0)
    .map((dist) => dist + ' km')
    .concat(triathlonEventLevel2Categories)

  const today = startOfDay(new Date())
  const isOver = endDate
    ? startOfDay(endDate) < today
    : startDate
    ? startOfDay(startDate) <= today
    : false

  const eventTypes = useMemo(
    () =>
      editionLiveLevel1CategoryKey
        .map((type) =>
          getEventTypeStyle(type as keyof typeof raceCategoriesLevel1)
        )
        .filter(isNotNullOrUndefined),
    [editionLiveLevel1CategoryKey]
  )

  return { distanceLabels, dateDisplayText, isOver, eventTypes }
}
